import * as React from 'react';
import { AppLink } from 'mydish-ui-util';
import formatter from '../utils/formatter-util';
import Card from './Card';
import KeyArtPoster from './KeyArtPoster';
import expandArrow from '../css/assets/img/day-ghost-block-arrow-inactive.svg';
import KeyArtPosterSwiperCarousel from './KeyArtPosterSwiperCarousel';
import '../css/poster.sass';
import Anchor from './Anchor';

type ResponsiveType = {
  desktop: number;
  mobile: number;
};

interface EventData {
  eventId: string;
  title: string;
  imageUrl: string;
  dnisStartDate: string;
  date?: string;
  genres?: string;
  ppvRatings?: any;
  price?: string;
  detailLink?: string;
  description?: string;
  futureRelease?: boolean;
}

interface PosterProps {
  eventData: EventData[];
  isExpandablePoster?: boolean;
  showEventDetails?: boolean;
  type: string;
  displayName?: string;
  slidesPerView?: ResponsiveType;
  slidesPerGroup?: ResponsiveType;
  showSwiperCarousel?: boolean;
  infiniteScroll?: boolean;
  showArrow?: boolean;
  isAnchor?: boolean;
  futureReleaseMessage?: React.ReactNode;
  theme?: 'night' | 'day';
  ignoreMinEventsForSwiper?: boolean;
  id?: string;
  category?: string;
  summaryEventId?: string;
  eventDetailUrl?: string;
}

export default function Poster(props: PosterProps) {
  const {
    theme = 'day',
    isExpandablePoster = false,
    showEventDetails = false,
    slidesPerView = { desktop: 6, mobile: 2 },
    slidesPerGroup = { desktop: 6, mobile: 2 },
    showSwiperCarousel = true,
    isAnchor = false
  } = props;

  const eventDetails = (eventInfo: EventData, index: number) => {
    return (
      <div className="event-details">
        {!isExpandablePoster && (
          <div className="event-data-poster mt-3">
            {isAnchor ? (
              <Anchor
                id={`${props.category ? `${props.category}-` : ''}${
                  eventInfo.title
                }-${index}`}
                data-testid={eventInfo.title.trim()}
                href={`${eventInfo.detailLink || props.eventDetailUrl}?type=${
                  props.type
                }${
                  props.summaryEventId
                    ? `&summaryEventId=${props.summaryEventId}`
                    : `&eventId=${eventInfo.eventId}`
                }`}
                arialabel={eventInfo.title}
                removeContainerSpan
              >
                {eventInfo.title}
              </Anchor>
            ) : (
              eventInfo.title
            )}
            {props.showArrow && !showEventDetails && (
              <AppLink
                href={`${eventInfo.detailLink || props.eventDetailUrl}?type=${
                  props.type
                }${
                  props.summaryEventId
                    ? `&summaryEventId=${props.summaryEventId}`
                    : `&eventId=${eventInfo.eventId}`
                }`}
              >
                <img
                  className="ml-2"
                  id="expand-content"
                  data-testid={'expand-content-'.concat(index.toString())}
                  src={expandArrow}
                  alt="expand details"
                  role="none"
                />
              </AppLink>
            )}
          </div>
        )}
        {showEventDetails &&
          eventInfo.ppvRatings &&
          eventInfo.ppvRatings.eventRating && (
            <div className="item-info rating">
              <div
                key={eventInfo.ppvRatings.eventRating.toString()}
                className="type"
              >
                {eventInfo.ppvRatings.eventRating}
              </div>
            </div>
          )}
        {showEventDetails && (
          <div className="item-info">
            {eventInfo.date && (
              <div>
                {formatter({
                  type: 'date',
                  format: 'EEE, MM/dd/yy',
                  value: eventInfo.date,
                  inputFormat: 'EEE, MM/dd/yy'
                })}
              </div>
            )}
            {eventInfo.price && (
              <div className="item-price">
                {formatter({
                  type: 'number',
                  format: 'amount',
                  value: eventInfo.price
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const eventDataItems = () => {
    return (
      props.eventData &&
      Array.isArray(props.eventData) &&
      props.eventData.length > 0 &&
      props.eventData.map((eventInfo: EventData, index: number) => (
        <div
          className="event-poster"
          key={eventInfo.title.concat('--').concat(index.toString())}
        >
          <div
            // eslint-disable-next-line jsx-a11y/interactive-supports-focus
            role="button"
            tabIndex={-1}
            data-testid={'expand-content-key-art-'.concat(index.toString())}
          >
            <AppLink
              href={`${eventInfo.detailLink || props.eventDetailUrl}?type=${
                props.type
              }${
                props.summaryEventId
                  ? `&summaryEventId=${props.summaryEventId}`
                  : `&eventId=${eventInfo.eventId}`
              }`}
            >
              <KeyArtPoster
                imageUrl={eventInfo.imageUrl}
                title={eventInfo.title}
                id={`${props.category ? `${props.category}-` : ''}${
                  eventInfo.title
                }${index}`}
                focusable
                expandable={isExpandablePoster}
                nextAiringOn={
                  eventInfo.futureRelease &&
                  eventInfo.date &&
                  props.futureReleaseMessage && (
                    <>
                      {props.futureReleaseMessage}
                      {formatter({
                        type: 'date',
                        format: 'MMMM dd',
                        value: eventInfo.date,
                        inputFormat: 'EEE, MM/dd/yy'
                      })}
                    </>
                  )
                }
                theme={props.theme}
                showEventDetails={props.showArrow && showEventDetails}
              />
            </AppLink>
          </div>
          {isExpandablePoster && <Card>{eventDetails(eventInfo, index)}</Card>}
          {!isExpandablePoster && eventDetails(eventInfo, index)}
        </div>
      ))
    );
  };

  return (
    <div className={`poster ${theme}`}>
      {props.displayName && <h1>{props.displayName}</h1>}
      <div className={`${showSwiperCarousel ? '' : 'no-swiper-carousel'}`}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {showSwiperCarousel &&
        (props.eventData.length > 6 || props.ignoreMinEventsForSwiper) ? (
          // @ts-ignore
          <KeyArtPosterSwiperCarousel
            id={props.id}
            slidesPerView={slidesPerView}
            slidesPerGroup={slidesPerGroup}
            infiniteScroll={props.infiniteScroll}
            key={props.type}
          >
            {eventDataItems()}
          </KeyArtPosterSwiperCarousel>
        ) : showSwiperCarousel ? (
          <div className="no-swiper-carousel no-wrap">{eventDataItems()}</div>
        ) : (
          eventDataItems()
        )}
      </div>
    </div>
  );
}
